import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AIAssistantHeader } from './AIAssistantHeader';
import { AIInput } from './AIInput';
import { OrbitField } from './OrbitField';
import { AIChat } from './AIChat';
import { ChatHistory } from './ChatHistory';
import type { RevealState } from '../model/useRevealController';
import { useAiAssistant } from '../model/useAiAssistant';
import { useAiChatStore } from '../model/aiChatStore';
import { useAiAvailabilityStore } from '../model/aiAvailabilityStore';
import styles from './AIAssistantReveal.module.css';
import { Scroller } from '@/shared/ui';

export interface AIAssistantRevealProps {
  state: RevealState;
  onClose: () => void;
}

export const AIAssistantReveal: React.FC<AIAssistantRevealProps> = ({ state, onClose }) => {
  const open = state === 'open';
  const active = state !== 'collapsed';
  const { send, rate, newChat, openChat } = useAiAssistant(active);
  const hasMessages = useAiChatStore((s) => s.messages.length > 0);
  const busy = useAiChatStore((s) => s.status !== 'idle');
  const indexEmpty = useAiAvailabilityStore((s) => s.problems.includes('index_empty'));
  const [showHistory, setShowHistory] = useState(false);
  const screenRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    screenRef.current?.toggleAttribute('inert', state === 'collapsed');
  }, [state]);

  return (
    <div ref={screenRef} className={styles.screen} data-ai-screen>
      <AIAssistantHeader
        onOpenHistory={() => setShowHistory(true)}
        onNewChat={newChat}
        showNewChat={hasMessages}
      />
      <div className={styles.stars} aria-hidden />

      <div className={styles.panel}>
        {hasMessages ? (
          <>
            <Scroller
              className={styles.contentScroll}
              data-ai-scroll
            >
              <AIChat onRate={rate} onNewChat={newChat} />
            </Scroller>
            <div className={styles.inputDock}>
              <AIInput onSubmit={send} autoFocus={open} disabled={busy} />
            </div>
          </>
        ) : (
          <div className={styles.contentArea}>
            <div className={styles.center}>
              { }
              <div className={styles.iconWrap} aria-hidden>
                <OrbitField />
              </div>
              <h1 className={styles.title}>С чем вам помочь?</h1>
              <div className={styles.heroInput}>
                <AIInput onSubmit={send} autoFocus={open} disabled={busy} />
                {indexEmpty && (
                  <p className={styles.hint}>
                    База знаний помощника ещё не загружена — ответить по инструкциям пока не получится.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        { }
        <div className={styles.exitDock}>
          <button type="button" className={styles.exitBtn} onClick={onClose}>
            <ChevronDown size={18} aria-hidden />
            Свернуть
          </button>
        </div>
      </div>

      {showHistory && (
        <>
          <div className={styles.backdrop} onClick={() => setShowHistory(false)} />
          <div className={styles.drawer}>
            <ChatHistory
              onOpen={(id) => {
                openChat(id);
                setShowHistory(false);
              }}
              onNew={() => {
                newChat();
                setShowHistory(false);
              }}
              onClose={() => setShowHistory(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};
